import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TrafficCone } from 'lucide-react';
import debounce from 'lodash/debounce';
import { DestinationSwitch } from '@/components/inspector/destination-accordion/destination-switch';
import { useDestinationsList } from '@/hooks/destinations/useDestinations';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type InspectorTabs } from '@/components/inspector/inspector';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { setDestination } from '@/services/destination.service';
import { type NodeType, type TheNode } from '@/types/tree';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const DestinationAccordion = ({
  disabled,
  item,
  currentInspectorTab,
  currentSelectedType,
  readOnly = false,
  enabled,
}: {
  disabled: boolean;
  item?: TheNode;
  currentInspectorTab: InspectorTabs;
  currentSelectedType?: NodeType;
  readOnly?: boolean;
  enabled: boolean;
}) => {
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [enabledDestinations, setEnabledDestinations] = useState<Array<string>>([]);

  const { toast } = useToast();
  const tCommon = useTranslations('common');
  const tDestinations = useTranslations('inspector.destinations');
  const tToasts = useTranslations('toasts');
  const {
    isPending: destinationListIsPending,
    data: destinations,
    refetch: refetchDestinationList,
  } = useDestinationsList(
    {
      pagination: { limit: 200, offset: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
      queryString: '',
    },
    {
      enabled: enabled,
    },
  );

  const setItemDestinationWithAuth = useAuthenticatedQueryFn(setDestination);

  const setItemDestinationMutation = useMutation({
    mutationFn: setItemDestinationWithAuth,
    onSuccess: async () => {
      const AssetQueryKeyPrefix = currentSelectedType === 'assets' ? 'asset' : 'album';

      await queryClient.invalidateQueries({ queryKey: [AssetQueryKeyPrefix] });

      toast({
        title: tToasts('setItemDestinationMutation.title'),
        descriptionClassName: 'flex min-w-0 whitespace-pre',
        description: tToasts.rich('setItemDestinationMutation.description', {
          itemName: item?.name ?? '',
          highlight: (chunks) => <ToastHighlight chunks={chunks} />,
        }),
      });
    },
    onError: (err) => {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tToasts('setItemDestinationMutation.errorDescription'),
      });
    },
  });

  useEffect(() => {
    if (currentInspectorTab?.includes('destination')) {
      void refetchDestinationList();
    }
    if (item?.destinations) {
      setEnabledDestinations(item?.destinations);
    }
  }, [item?.destinations]);

  const debouncedSetItemDestinationMutation = useCallback(
    debounce((updatedDestinations) => {
      setItemDestinationMutation.mutate({
        destinationIds: updatedDestinations,
        itemId: item?.id,
        type: currentSelectedType,
      });
    }, 500),
    [item?.id],
  );

  const handleDestinationToggle = (id: string, e: boolean) => {
    setEnabledDestinations((prevEnabledDestinations) => {
      const updatedDestinations = e
        ? Array.from(new Set([...prevEnabledDestinations, id]))
        : prevEnabledDestinations.filter((destinationId) => destinationId !== id);

      debouncedSetItemDestinationMutation(updatedDestinations);

      return updatedDestinations;
    });
  };

  return destinationListIsPending ? (
    Array.from(Array(2)).map((_, index) => (
      <div className="flex w-full gap-x-3 p-4" key={index}>
        <Skeleton className="max-h-5 min-h-5 w-9 max-w-9 min-w-9 rounded-full" />
        <Skeleton className={cn('h-5', index === 0 ? 'w-3/6' : 'w-2/5')} />
      </div>
    ))
  ) : destinations && destinations.length > 0 ? (
    <div className="space-y-3 p-4">
      {destinations?.map(({ id, name }: { id: string; name: string }) => (
        <DestinationSwitch
          key={id}
          id={id}
          handleDestinationToggle={handleDestinationToggle}
          checked={Boolean(item?.destinations?.find((destinationId) => destinationId === id))}
          disabled={readOnly || disabled}
        >
          {name}
        </DestinationSwitch>
      ))}
    </div>
  ) : (
    <div className="p-4 py-1 text-center text-neutral-600">
      <TrafficCone className="mx-auto mb-2" size={28} strokeWidth={1.5} />
      {tDestinations('empty')}
    </div>
  );
};
